/**
 * CookieConsentBanner
 *
 * Fixed bottom banner for cookie consent (GDPR/CCPA).
 * Based on Doc 14: Legal Policies.
 */

import { useState } from 'react'
import { useLegalStore } from '../../store'
import type { CookiePreferences } from '../../types'
import { Button } from '../ui/button'

export function CookieConsentBanner() {
  const consentStatus = useLegalStore((s) => s.consentStatus)
  const showBanner = useLegalStore((s) => s.showBanner)
  const acceptAll = useLegalStore((s) => s.acceptAll)
  const rejectAll = useLegalStore((s) => s.rejectAll)
  const customizePreferences = useLegalStore((s) => s.customizePreferences)
  const [showCustomize, setShowCustomize] = useState(false)
  const [customPrefs, setCustomPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  if (!showBanner || consentStatus !== 'pending') return null

  if (showCustomize) {
    return (
      <div data-figma-ignore className="fixed bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-[1300] p-4">
        <div className="max-w-4xl mx-auto">
          <p className="font-semibold mb-3 text-foreground">Cookie Preferences</p>
          <div className="flex flex-col gap-2 mb-4">
            <div className="flex justify-between items-center p-2 bg-background-secondary rounded-[var(--radius-button)]">
              <span className="text-sm text-foreground">Essential Cookies</span>
              <span className="text-xs text-foreground-muted">Always on</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-background-secondary rounded-[var(--radius-button)]">
              <span className="text-sm text-foreground">Analytics Cookies</span>
              <Button
                size="sm"
                variant={customPrefs.analytics ? 'default' : 'outline'}
                onClick={() => setCustomPrefs({ ...customPrefs, analytics: !customPrefs.analytics })}
              >
                {customPrefs.analytics ? 'On' : 'Off'}
              </Button>
            </div>
            <div className="flex justify-between items-center p-2 bg-background-secondary rounded-[var(--radius-button)]">
              <span className="text-sm text-foreground">Marketing Cookies</span>
              <Button
                size="sm"
                variant={customPrefs.marketing ? 'default' : 'outline'}
                onClick={() => setCustomPrefs({ ...customPrefs, marketing: !customPrefs.marketing })}
              >
                {customPrefs.marketing ? 'On' : 'Off'}
              </Button>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowCustomize(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => customizePreferences(customPrefs)}
            >
              Save Preferences
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div data-figma-ignore data-figma-component="CookieConsentBanner" className="fixed bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-[1300] p-4">
      <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-stretch md:items-center gap-4">
        <p className="text-sm text-foreground-muted flex-1">
          We use cookies to improve your experience. By continuing to use DoCurious,
          you agree to our use of cookies. You can customize your preferences at any time.
        </p>
        <div className="flex gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => setShowCustomize(true)}>
            Customize
          </Button>
          <Button variant="outline" size="sm" onClick={rejectAll}>
            Reject All
          </Button>
          <Button
            size="sm"
            onClick={acceptAll}
          >
            Accept All
          </Button>
        </div>
      </div>
    </div>
  )
}
